import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  draggable,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  Box,
  Column,
  HiddenFormLabel,
  NumberInput,
  Row,
  Text,
} from "@hightouch/htui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  chartFontStyles,
  getXAxisStyles,
  getYAxisStyles,
} from "src/components/charts/chart-utils";
import { renderCartesianGrid } from "src/components/charts/chart-grid";
import { ChartTooltip } from "src/components/charts/chart-tooltip";
import { averageYValueInclusiveToString } from "src/components/explore/visual/number-range-utils";

const draggableHandleWidth = 12;

interface Position {
  top: number;
  left: number;
  height: number;
}

interface DraggableHandleProps {
  id: "min" | "max";
  position?: Position;
  isReady: boolean;
  onHoverChange: (isHoveringOver: boolean) => void;
  zIndex?: number;
}

const DraggableHandle = ({
  id,
  position,
  isReady,
  onHoverChange,
  zIndex,
}: DraggableHandleProps) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !isReady) return;
    return draggable({
      element: el,
      getInitialData: () => ({ id }),
    });
  }, [id, isReady]);

  if (!position) return null;

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        width: `${draggableHandleWidth}px`,
        height: `${position.height}px`,
        transform: "translateX(-50%)",
        cursor: "ew-resize",
        zIndex,
      }}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    />
  );
};

interface TooltipContextValue {
  tooltipOverride?: { value: any; label: any };
  tooltipLabel: string;
}

const TooltipContext = React.createContext<TooltipContextValue | undefined>(
  undefined,
);

const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);
  if (!context)
    throw new Error(
      "NuberRangeInputWithChart: missing internal tooltip context",
    );
  return context;
};

const CustomTooltip = ({
  active,
  payload,
  label: labelProp,
}: {
  active?: boolean;
  payload?: any;
  label?: any;
}) => {
  let isVisible = active && payload && payload.length;

  let payloadValue = payload[0]?.value;
  let label = labelProp;

  // The reference lines are done outside of and on top of recharts because the drag events conflict with the recharts events.
  // So we need to manually trigger tooltips when hovering over the reference lines.
  const { tooltipOverride, tooltipLabel } = useTooltipContext();
  if (!isVisible && tooltipOverride) {
    isVisible = true;
    payloadValue = tooltipOverride.value;
    label = tooltipOverride.label;
  }

  const value =
    typeof payloadValue === "number"
      ? `${((payloadValue as number) * 100).toFixed(1)}%`
      : "";

  return (
    <div style={{ visibility: isVisible ? "visible" : "hidden" }}>
      {isVisible && (
        <ChartTooltip
          wrapperVariant="full"
          color="indigo.400"
          title={`${label} percentile`}
          subtitles={[tooltipLabel]}
          value={[{ value: value }]}
        />
      )}
    </div>
  );
};

export interface NumberRangeInputWithChartProps<Data extends object> {
  value: { min: number; max: number };
  onChange: (value: { min: number; max: number }) => void;
  disableTopSummary?: boolean;
  data: Data[];
  xKey: keyof Data & string;
  yKey: keyof Data & string;
  firstNumberInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  totalDataCount: number;
  yAxisLabel?: string;
  rateLabel?: string;
  tooltipLabel?: string;
}

const maxRetries = 10;

export const NumberRangeInputWithChart = <T extends object>(
  props: NumberRangeInputWithChartProps<T>,
) => {
  const {
    value,
    onChange,
    disableTopSummary,
    data,
    xKey,
    yKey,
    firstNumberInputRef,
    totalDataCount,
    yAxisLabel = "Likelihood to convert",
    rateLabel = "conversion rate",
    tooltipLabel = "Conversion rate",
  } = props;
  const chartRef = useRef<HTMLDivElement>(null);
  const [plotArea, setPlotArea] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [num, setNum] = useState(0);
  const [isHoveringOverRefLine1, setIsHoveringOverRefLine1] = useState(false);
  const [isHoveringOverRefLine2, setIsHoveringOverRefLine2] = useState(false);

  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const plotAreaRef = useRef(plotArea);

  const averageYValue = useMemo(
    () => averageYValueInclusiveToString(data, yKey, value.min, value.max),
    [value, data, yKey],
  );

  const numUsers = useMemo(
    () => (totalDataCount * (value.max - value.min)) / 100,
    [value, totalDataCount],
  );

  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
    plotAreaRef.current = plotArea;
  });

  const handleInputChange = (e: number | undefined, part: "min" | "max") => {
    // Update the input field's value immediately for a responsive feel

    // Parse both values to check for validity
    // Only update the chart's range if BOTH numbers are valid AND the range is logical (from <= to)
    const newInputValue = { ...value, [part]: e };
    const { min, max } = newInputValue;
    if (!isNaN(min) && !isNaN(max) && min <= max && min >= 0 && max <= 100) {
      onChange({ min: Math.floor(min), max: Math.floor(max) });
    }
  };

  const retry = useCallback(() => {
    setTimeout(() => setNum((n) => (n < maxRetries ? n + 1 : n)), 200);
  }, []);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    const chartElement = chartRef.current;
    if (!chartElement) {
      retry();
      return;
    }
    const grid = chartElement.querySelector(".recharts-cartesian-grid");
    if (!grid) {
      retry();
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const rect = entry.target.getBoundingClientRect();
        const parentRect = chartElement.getBoundingClientRect();
        setPlotArea({
          x: rect.left - parentRect.left,
          y: rect.top - parentRect.top,
          width: rect.width,
          height: rect.height,
        });
      }
    });
    observer.observe(grid);
    return () => observer.disconnect();
  }, [num, retry]);

  useEffect(() => {
    return monitorForElements({
      onDrag({ source, location }) {
        const currentPlotArea = plotAreaRef.current;
        const currentValue = valueRef.current;
        const currentOnChange = onChangeRef.current;

        if (!currentPlotArea || !chartRef.current) return;

        const id = source.data.id;
        const clientX = location.current.input.clientX;
        const chartRect = chartRef.current.getBoundingClientRect();
        const mouseXInChart = clientX - chartRect.left;
        const mouseXInPlotArea = mouseXInChart - currentPlotArea.x;

        const percentage = Math.max(
          0,
          Math.min(1, mouseXInPlotArea / currentPlotArea.width),
        );
        let newPercentile = Math.round(percentage * 100);

        if (id === "min") {
          newPercentile = Math.min(newPercentile, currentValue.max);
          currentOnChange({ ...currentValue, min: newPercentile });
        } else {
          newPercentile = Math.max(newPercentile, currentValue.min);
          currentOnChange({ ...currentValue, max: newPercentile });
        }
      },
    });
  }, []);

  let minHandlePosition: Position | undefined = undefined;
  let maxHandlePosition: Position | undefined = undefined;
  if (plotArea) {
    minHandlePosition = {
      left: plotArea.x + (value.min / 100) * plotArea.width,
      top: plotArea.y,
      height: plotArea.height,
    };
    maxHandlePosition = {
      left: plotArea.x + (value.max / 100) * plotArea.width,
      top: plotArea.y,
      height: plotArea.height,
    };
  }

  const referenceLineColor = "var(--chakra-colors-indigo-base)";
  const baseAreaLineColor = "var(--chakra-colors-indigo-500)";
  const activeAreaLineColor = "var(--chakra-colors-indigo-700)";
  const highlightFillColor = "rgba(172, 97, 255, 0.80)"; // Translucent version for the fill
  const baseFillColor = "rgba(172, 97, 255, 0.20)"; // A lighter, more translucent blue for the base

  const clipGradientId = "clipGradient";
  const strokeGradientId = "strokeGradient";

  // Calculate the gradient offsets based on the chart range (0-100)
  const fromOffset = value.min / 100;
  const toOffset = value.max / 100;

  return (
    <Column width="100%" minWidth="450px" maxWidth="800px" gap={2}>
      {!disableTopSummary && (
        <Row justifyContent="space-between">
          <Row>
            <Text mr={1}>{numUsers.toFixed(0)}</Text> users
          </Row>
          <Row>
            <Text mr={1}>{averageYValue}</Text> {rateLabel}
          </Row>
        </Row>
      )}

      <Row justifyContent="center" mb={2} alignItems="center">
        <Text>Analyzing users in the</Text>
        <Box width={12}>
          <HiddenFormLabel label="Minimum percentage">
            <NumberInput
              ref={firstNumberInputRef}
              value={value.min}
              onChange={(e) => handleInputChange(e, "min")}
              width="100%"
              size="sm"
              ml={1}
              mr={1}
            />
          </HiddenFormLabel>
        </Box>
        <Text>% to</Text>
        <Box width={12}>
          <HiddenFormLabel label="Maximum percentage">
            <NumberInput
              value={value.max}
              onChange={(e) => handleInputChange(e, "max")}
              width="100%"
              size="sm"
              ml={1}
              mr={1}
            />
          </HiddenFormLabel>
        </Box>
        <Text mr={1}>%</Text>
        percentile
      </Row>

      <Box
        ref={chartRef}
        width="100%"
        height="300px"
        position="relative"
        style={{ userSelect: "none" }}
      >
        <TooltipContext.Provider
          value={{
            tooltipOverride: isHoveringOverRefLine1
              ? { label: value.min, value: data[Math.floor(value.min)]?.[yKey] }
              : isHoveringOverRefLine2
                ? {
                    label: value.max,
                    value: data[Math.floor(value.max)]?.[yKey],
                  }
                : undefined,
            tooltipLabel,
          }}
        >
          <ResponsiveContainer>
            <AreaChart
              data={data}
              margin={{
                top: 0,
                right: 20,
                left: 0,
                bottom: 0,
              }}
            >
              <defs>
                {/* This gradient acts as a "clipping mask" for the solid color fill. */}
                <linearGradient id={clipGradientId} x1="0" y1="0" x2="1" y2="0">
                  <stop
                    offset={fromOffset}
                    stopColor={highlightFillColor}
                    stopOpacity={0}
                  />
                  <stop
                    offset={fromOffset}
                    stopColor={highlightFillColor}
                    stopOpacity={1}
                  />
                  <stop
                    offset={toOffset}
                    stopColor={highlightFillColor}
                    stopOpacity={1}
                  />
                  <stop
                    offset={toOffset}
                    stopColor={highlightFillColor}
                    stopOpacity={0}
                  />
                </linearGradient>
                {/* Clipping mask gradient for the stroke */}
                <linearGradient
                  id={strokeGradientId}
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset={fromOffset} stopColor="transparent" />
                  <stop offset={fromOffset} stopColor={activeAreaLineColor} />
                  <stop offset={toOffset} stopColor={activeAreaLineColor} />
                  <stop offset={toOffset} stopColor="transparent" />
                </linearGradient>
              </defs>
              {renderCartesianGrid({ vertical: true })}
              <XAxis
                {...getXAxisStyles({ tickAlign: "center" })}
                dataKey={xKey}
                type="number"
                label={{
                  value: "Percentile",
                  position: "insideBottom",
                  offset: 2,
                  ...chartFontStyles,
                }}
                height={40}
                tickCount={21}
              />
              <YAxis
                {...getYAxisStyles({ allowDecimals: true })}
                label={{
                  value: yAxisLabel,
                  angle: 90,
                  position: "right",
                  style: { textAnchor: "middle" },
                  offset: 6,
                  ...chartFontStyles,
                }}
                type="number"
                domain={[0, 1.0]}
              />
              <Tooltip content={CustomTooltip} />
              {/* 1. Base Area (bottom layer) with a light, solid blue fill */}
              <Area
                type="monotone"
                dataKey={yKey}
                stroke={baseAreaLineColor}
                strokeWidth={1}
                fill={baseFillColor}
              />

              {/* 2. Highlighted Area (top layer) uses the clipping gradient */}
              <Area
                type="monotone"
                dataKey={yKey}
                stroke={`url(#${strokeGradientId})`}
                strokeWidth={2}
                fill={`url(#${clipGradientId})`}
                tooltipType="none"
              />
              <ReferenceLine
                x={value.min}
                style={{
                  // Keep reference line inside chart bounds
                  transform: value.min === 0 ? "translateX(1px)" : undefined,
                }}
                ifOverflow="visible"
                stroke={referenceLineColor}
                strokeOpacity={0.9}
                strokeWidth={2}
              />
              <ReferenceLine
                x={value.max}
                style={{
                  // Keep reference line inside chart bounds
                  transform: value.max === 100 ? "translateX(-1px)" : undefined,
                }}
                ifOverflow="visible"
                stroke={referenceLineColor}
                strokeOpacity={0.9}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </TooltipContext.Provider>
        <DraggableHandle
          id="min"
          position={minHandlePosition}
          // There's some odd timing issue between recharts and pdnd, so we need to wait
          // until the plot area is ready before registering the draggable hook
          isReady={!!plotArea}
          onHoverChange={setIsHoveringOverRefLine1}
          /*
            When both min and max are 100, the max draggable handle covers the min draggable handle
            but the max handle can't be moved since it's bounded between the min handle and 100.
            So we move the min handle on top in only this case.
          */
          zIndex={value.min === 100 ? 1 : 0}
        />
        <DraggableHandle
          id="max"
          position={maxHandlePosition}
          isReady={!!plotArea}
          onHoverChange={setIsHoveringOverRefLine2}
        />
      </Box>
    </Column>
  );
};
